import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { User, EnvelopeSimple, Phone, PencilSimple } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfilAMO() {
  const { user, checkAuth } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/users/profile`, {
        name: form.name,
        phone: form.phone,
      }, { withCredentials: true });
      toast.success('Profil mis à jour');
      setEditing(false);
      checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-2xl" data-testid="profil-amo-page">
        <div className="mb-8">
          <h1 className="text-3xl text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Mon profil
          </h1>
          <p className="text-[#6B6B6B] mt-1">
            Gérez vos informations personnelles
          </p>
        </div>

        <div className="card-kroquis">
          <div className="flex items-center gap-6 mb-8">
            <div className="w-20 h-20 bg-[#2D6A4F] text-white flex items-center justify-center text-2xl font-bold">
              {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-[#2C2C2C]">{user?.name}</h2>
              <p className="text-sm text-[#C75B39] font-medium">Assistance à Maîtrise d'Ouvrage</p>
              <p className="text-xs text-[#6B6B6B] mt-1">{user?.email}</p>
            </div>
          </div>

          {editing ? (
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <Label>Nom complet</Label>
                <Input 
                  value={form.name} 
                  onChange={e => setForm({...form, name: e.target.value})} 
                  required 
                  className="rounded-none" 
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  value={form.email} 
                  disabled 
                  className="rounded-none bg-[#F5F5F3]" 
                />
                <p className="text-xs text-[#6B6B6B] mt-1">L'email ne peut pas être modifié</p>
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input 
                  value={form.phone} 
                  onChange={e => setForm({...form, phone: e.target.value})} 
                  placeholder="06 12 34 56 78" 
                  className="rounded-none" 
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditing(false)} className="rounded-none">
                  Annuler
                </Button>
                <Button type="submit" className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none">
                  Enregistrer
                </Button>
              </div>
            </form>
          ) : (
            <>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-[#F5F5F3] border border-[#E0E0DB]">
                  <User size={18} className="text-[#6B6B6B]" />
                  <div>
                    <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Nom</p>
                    <p className="font-medium text-[#2C2C2C]">{user?.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-[#F5F5F3] border border-[#E0E0DB]">
                  <EnvelopeSimple size={18} className="text-[#6B6B6B]" />
                  <div>
                    <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Email</p>
                    <p className="font-medium text-[#2C2C2C]">{user?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-[#F5F5F3] border border-[#E0E0DB]">
                  <Phone size={18} className="text-[#6B6B6B]" />
                  <div>
                    <p className="text-xs text-[#6B6B6B] uppercase tracking-wider">Téléphone</p>
                    <p className="font-medium text-[#2C2C2C]">{user?.phone || 'Non renseigné'}</p>
                  </div>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="mt-6 gap-2 rounded-none"
                onClick={() => setEditing(true)}
                data-testid="edit-profile-button"
              >
                <PencilSimple size={16} />
                Modifier mon profil
              </Button>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
